import React, { useState, useEffect } from "react";
import Header from "parts/Header";
import Footer from "parts/Footer";
import { Fade } from "react-awesome-reveal";
import Discuss from "parts/Discuss";
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  Target,
  Users,
  TrendingUp,
  Zap,
  Globe,
  Sparkles,
  ArrowRight,
  Play,
  ChevronDown,
} from "lucide-react";

const MarketingAndClientAcquisitionStrategyPage = () => {
  const [activeStrategy, setActiveStrategy] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    console.log(isVisible);
  }, []);

  const strategies = [
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Digital Ecosystem Mastery",
      description:
        "AI-powered LinkedIn campaigns, precision Google Ads targeting high-growth startups, and viral TikTok content for Gen-Z founders",
      metric: "300% ROI",
      color: "from-blue-500 to-purple-600",
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Community-Driven Events",
      description:
        "Exclusive founder dinners, pitch competitions with $1M+ prizes, and virtual reality networking experiences",
      metric: "50K+ attendees",
      color: "from-green-500 to-teal-600",
    },
    {
      icon: <Target className="w-8 h-8" />,
      title: "Strategic Partnerships",
      description:
        "Deep integrations with Y Combinator, Techstars, and emerging accelerators across 15+ countries",
      metric: "200+ partners",
      color: "from-orange-500 to-red-600",
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Thought Leadership",
      description:
        "Weekly podcasts with unicorn founders, viral market analysis threads, and exclusive industry reports",
      metric: "10M+ views",
      color: "from-purple-500 to-pink-600",
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: "Referral Revolution",
      description:
        "Gamified referral system with crypto rewards, NFT exclusive access, and founder-to-founder incentives",
      metric: "85% referral rate",
      color: "from-yellow-500 to-orange-600",
    },
  ];

  const stats = [
    {
      number: "500+",
      label: "Portfolio Companies",
      icon: <Target className="w-6 h-6" />,
    },
    {
      number: "$2.5B",
      label: "Assets Under Management",
      icon: <TrendingUp className="w-6 h-6" />,
    },
    {
      number: "15x",
      label: "Average Return",
      icon: <Sparkles className="w-6 h-6" />,
    },
    {
      number: "98%",
      label: "Client Satisfaction",
      icon: <Users className="w-6 h-6" />,
    },
  ];

  return (
    <>
      <Header />
      <div className="overflow-hidden bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        {/* Animated Background Elements */}

        {/* Hero Section */}
        <div className="container relative px-6 pt-20 pb-12 mx-auto">
          <Fade cascade triggerOnce>
            <div className="mb-16 text-center">
              <div className="inline-flex items-center px-6 py-3 mb-8 border rounded-full bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm border-white/10">
                <Sparkles className="w-5 h-5 mr-2 text-yellow-400 animate-spin" />
                <span className="text-sm font-medium text-white/90">
                  Next-Gen Growth Strategy
                </span>
              </div>

              <h1 className="mb-6 text-6xl font-black leading-tight text-transparent md:text-8xl bg-clip-text bg-gradient-to-r from-white via-blue-200 to-purple-200">
                Growth
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Reimagined
                </span>
              </h1>

              <p className="max-w-3xl mx-auto mb-8 text-xl leading-relaxed text-white/70">
                We don&apos;t just acquire clients—we build ecosystems. Our
                revolutionary approach combines cutting-edge technology with
                human-centered design to create unstoppable growth momentum.
              </p>

              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <button
                  type="button"
                  className="flex items-center px-8 py-4 font-semibold text-white transition-all duration-300 transform rounded-full group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 hover:shadow-2xl"
                >
                  <Play className="w-5 h-5 mr-2 group-hover:animate-pulse" />
                  Watch Our Story
                </button>
                <button
                  type="button"
                  className="flex items-center px-8 py-4 font-semibold text-white transition-all duration-300 border rounded-full group bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20"
                >
                  Explore Strategies
                  <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                </button>
              </div>
            </div>
          </Fade>
        </div>

        {/* Stats Section */}
        <div className="container relative z-10 px-6 mx-auto mb-20">
          <Fade triggerOnce>
            <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
              {stats.map((stat, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className="p-6 text-center transition-all duration-300 border group bg-white/5 backdrop-blur-sm border-white/10 rounded-2xl hover:bg-white/10 hover:scale-105"
                >
                  <div className="flex justify-center mb-3 text-blue-400 group-hover:animate-bounce">
                    {stat.icon}
                  </div>
                  <div className="mb-1 text-3xl font-bold text-white">
                    {stat.number}
                  </div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </Fade>
        </div>

        {/* Interactive Strategy Cards */}
        <div className="container relative z-10 px-6 mx-auto mb-20">
          <Fade triggerOnce>
            <div className="mb-12 text-center">
              <h2 className="mb-4 text-4xl font-bold text-white md:text-5xl">
                Our{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                  Strategy Arsenal
                </span>
              </h2>
              <p className="max-w-2xl mx-auto text-lg text-white/70">
                Five revolutionary approaches that have transformed how we
                connect with innovators worldwide
              </p>
            </div>

            <div className="grid gap-6 mx-auto md:grid-cols-2 lg:grid-cols-3 max-w-7xl">
              {strategies.map((strategy, index) => (
                <div
                  // eslint-disable-next-line react/no-array-index-key
                  key={index}
                  className={`group relative bg-gradient-to-br ${
                    strategy.color
                  } p-1 rounded-3xl transition-all duration-500 hover:scale-105 hover:rotate-1 cursor-pointer ${
                    activeStrategy === index ? "scale-105 rotate-1" : ""
                  }`}
                  onMouseEnter={() => setActiveStrategy(index)}
                >
                  <div className="h-full p-8 bg-slate-900/90 backdrop-blur-sm rounded-3xl">
                    <div className="mb-6 text-white group-hover:animate-pulse">
                      {strategy.icon}
                    </div>

                    <h3 className="mb-4 text-xl font-bold text-white transition-all duration-300 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-white group-hover:to-blue-200">
                      {strategy.title}
                    </h3>

                    <p className="mb-6 leading-relaxed text-white/70">
                      {strategy.description}
                    </p>

                    <div className="flex items-center justify-between">
                      <div className="px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm">
                        <span className="text-sm font-semibold text-white">
                          {strategy.metric}
                        </span>
                      </div>
                      <ArrowRight className="w-5 h-5 transition-all duration-300 text-white/50 group-hover:text-white group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Fade>
        </div>

        {/* CTA Section */}
        <div className="container relative z-10 px-6 pb-20 mx-auto">
          <Fade triggerOnce>
            <div className="p-12 text-center border bg-gradient-to-r from-blue-600/20 to-purple-600/20 backdrop-blur-sm border-white/10 rounded-3xl">
              <div className="flex justify-center mb-6">
                <div className="p-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl">
                  <Sparkles className="w-8 h-8 text-white animate-spin" />
                </div>
              </div>

              <h3 className="mb-4 text-3xl font-bold text-white md:text-4xl">
                Ready to Scale Beyond Limits?
              </h3>

              <p className="max-w-2xl mx-auto mb-8 text-lg text-white/70">
                Join the founders who&apos;ve already discovered the future of
                growth. Let&apos;s build something extraordinary together.
              </p>

              <div className="flex flex-col justify-center gap-4 sm:flex-row">
                <button
                  type="button"
                  className="flex items-center justify-center px-10 py-4 font-semibold text-white transition-all duration-300 transform rounded-full group bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 hover:scale-105 hover:shadow-2xl"
                >
                  Start Your Journey
                  <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                </button>
                <button
                  type="button"
                  className="flex items-center justify-center px-10 py-4 font-semibold text-white transition-all duration-300 border rounded-full group bg-white/10 backdrop-blur-sm border-white/20 hover:bg-white/20"
                >
                  Book a Strategy Call
                </button>
              </div>
            </div>
          </Fade>
        </div>

        {/* Scroll Indicator */}
        <div className="fixed z-20 transform -translate-x-1/2 bottom-8 left-1/2">
          <div className="p-3 border rounded-full bg-white/10 backdrop-blur-sm border-white/20 animate-bounce">
            <ChevronDown className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
      <Discuss />
      <Footer />
    </>
  );
};

export default MarketingAndClientAcquisitionStrategyPage;
